<template>
  <v-card
    :class="{
      'manage-devices-card': true,
      'fit-to-space': !isMobile,
      'ma-6': !xs,
      'is-mobile': isMobile
    }"
  >
    <v-card-title class="header-title pa-4">{{ $t('manageDevices.title') }}</v-card-title>
    <v-divider class="divider"></v-divider>
    <div class="table-bar">
      <v-text-field
        v-model="search"
        prepend-inner-icon="mdi-magnify"
        :label="$t('tables.search')"
        single-line
        hide-details
        class="search-field"
        data-cy="searchField"
      />
      <DropdownMenu
        v-if="!isLoading"
        data-cy="addDeviceDropdown"
        v-bind="getAddDevicesDropDownProps()"
      />
    </div>
    <!-- add 'show-select' prop for checkboxes -->
    <v-data-table
      v-if="showTable"
      :headers="columns"
      :items="devices"
      :mobile-breakpoint="thresholds.md"
      :class="{ mobile: isMobile }"
      :custom-filter="filterByColumnValueAndDeviceAttributes"
      :search="customTableFilter"
      v-model:sort-by="sortedBy"
      v-model:items-per-page="itemsPerPage"
      item-value="id"
      show-expand
      :single-expand="singleExpand"
      v-model:expanded="expanded"
      :items-per-page-options="[10, 25, 50]"
      :items-per-page-text="isMobile ? 'tables.rows' : 'tables.rowsPerPage'"
      fixed-header
      fixed-footer
      data-cy="manageDevicesTable"
      @update:current-items="updateTableComputedItems"
    >
      <template v-if="isMobile" v-slot:[`body.prepend`]>
        <ExpandCollapseButtons
          v-if="enableExpandCollapseButtons"
          @expandAll="expandAllAssociatedDevices"
          @collapseAll="collapseAllAssociatedDevices"
        />
      </template>
      <!-- table item slot -->
      <template v-slot:item="{ item, internalItem, isExpanded, toggleExpand }">
        <component
          :is="tableItemComponent"
          :item="item"
          :expand="() => toggleExpand(internalItem)"
          :isExpanded="isExpanded(internalItem)"
          @openDialog="openDialog"
        />
      </template>
      <!-- Show the expand button when a SmartBridge device has devices -->
      <template v-slot:expanded-row="{ item }">
        <component
          :is="tableItemComponent"
          v-for="i in item.associatedDevices || []"
          :item="i"
          :isChild="true"
          :key="i.id"
          @openDialog="openDialog"
        />
      </template>
      <template v-if="!isMobile" v-slot:[`footer.prepend`]>
        <ExpandCollapseButtons
          v-if="enableExpandCollapseButtons"
          @expandAll="expandAllAssociatedDevices"
          @collapseAll="collapseAllAssociatedDevices"
        />
      </template>
    </v-data-table>
    <!-- empty message -->
    <div v-else-if="showEmptyMessage" class="text-center emptyMessage">
      {{ $t('manageDevices.emptyMessage') }}
    </div>
    <!-- progress spinner -->
    <div v-else class="text-center emptyMessage">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <RegisterDevice
      :open="dialogs.registerDevice"
      :deviceSerial="''"
      :deviceModel="''"
      @close="dialogs.registerDevice = false"
    />
    <RegisterMultipleDevices
      :open="dialogs.registerMultipleDevices"
      @close="dialogs.registerMultipleDevices = false"
    />
    <ActionMenuDialogs
      :dialog="currentDialog"
      :deviceId="currentDeviceId"
      @closeDialog="closeDialog"
    />
  </v-card>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useDisplay } from 'vuetify'
import type { DataTableHeader, DataTableSortItem } from 'vuetify'
import { Access } from '@/services/device-access'
import { sortDeviceCondition } from '@/services/device-table'
import { validateTimestamp } from '@/services/device-status'
import { measurementTypesKey, MeasurementType } from '@/services/device-measurements'
import { aqiTypes } from '@/store/map/utils'
import { deviceModelDisplay, deviceSubmodelDisplay } from '@/services/map'
import { DEVICE_UPDATE_INTERVAL_MS } from '@/services/device-metadata'
import RegisterDevice from '@/components/registerDevice/registerDialog.vue'
import RegisterMultipleDevices from '@/components/registerDevice/multipleDevices/registerMultipleDevices.vue'
import DeviceTableBody from '@/components/manageDevices/tableBody.vue'
import DeviceTableBodyMobile from '@/components/manageDevices/tableBodyMobile.vue'
import DropdownMenu from '@/components/menu/dropDownMenu.vue'
import ActionMenuDialogs from '@/components/manageDevices/actionMenuDialogs.vue'
import ExpandCollapseButtons from '@/components/common/buttons/ExpandCollapseButtons.vue'
import { searchableDeviceAttributes, ConnectionType } from '@/store/devices/utils'
import { getUserAccountId } from '@/helpers/loginas/logInAsHelper'
import { subscriptionTypes } from '@/store/subscriptions/utils'
import { NAMESPACES } from '@/store/types'
import { DEVICE_ACTIONS, DEVICE_GETTERS } from '@/store/devices'
import { DEVICE_MODELS_GETTERS } from '@/store/devicemodels'
import { MAP_GETTERS } from '@/store/map'
import { SUBSCRIPTIONS_ACTIONS } from '@/store/subscriptions'
import { DEVICE_INTERVALS_ACTIONS } from '@/store/device-intervals'

const LOCAL_STORAGE_KEYS = 'tsi.devicesTableSettings'

export interface ManageDeviceRow {
  id: string
  showSubscription: boolean
  showCondition: boolean
  showAQI: boolean
  showLastCommunication: boolean
  deviceName: string
  deviceLocationName: string
  accessCode: Access
  access: string
  deviceType: string
  deviceModel: string
  deviceSerial: string
  deviceSubscription: string
  lastCommunication: string
  reading: number | null
  gatewaySerial?: string
  associatedDevices?: ManageDeviceRow[]
}

export default defineComponent({
  name: 'ManageDevices',
  setup() {
    const { xs, smAndDown, thresholds } = useDisplay()
    return { xs, smAndDown, thresholds }
  },
  components: {
    RegisterDevice,
    RegisterMultipleDevices,
    DeviceTableBody,
    DeviceTableBodyMobile,
    DropdownMenu,
    ActionMenuDialogs,
    ExpandCollapseButtons
  },
  async mounted(): Promise<void> {
    this.accountId = getUserAccountId(this.$auth)
    this.getDevicesTableSettings()
    await this.startUserDevicesInterval()
  },
  unmounted(): void {
    this.$store.dispatch(
      `${NAMESPACES.DEVICE_INTERVALS}/${DEVICE_INTERVALS_ACTIONS.CLEAR_INTERVAL}`
    )
    this.restartAppInterval()
  },
  data() {
    return {
      accountId: null as string | null,
      expanded: [] as string[],
      singleExpand: false,
      sortedBy: [{ key: 'deviceName', order: 'asc' }] as DataTableSortItem[],
      itemsPerPage: 10,
      firstSearch: true,
      search: '',
      access: Access,
      dialogs: {
        registerDevice: false,
        registerMultipleDevices: false
      },
      interval: null as NodeJS.Timeout | null,
      isDevicesFirstCallInProgress: true,
      tableComputedItems: [] as ManageDeviceRow[],
      currentDialog: '',
      currentDeviceId: ''
    }
  },
  watch: {
    sortedBy: {
      deep: true,
      handler(): void {
        this.setDevicesTableSettings()
      }
    },
    itemsPerPage(): void {
      this.setDevicesTableSettings()
    },
    search(): void {
      if (this.firstSearch) {
        this.firstSearch = false
      }
      this.collapseAllAssociatedDevices()
    }
  },
  methods: {
    getMeasurementType(measurementType: number): string {
      const isIndia = this.aqiType === aqiTypes.in
      switch (measurementType) {
        case MeasurementType.Pm25:
          return isIndia
            ? measurementTypesKey[MeasurementType.Pm25IndiaAqi]
            : measurementTypesKey[MeasurementType.Pm25]
        case MeasurementType.Pm10:
          return isIndia
            ? measurementTypesKey[MeasurementType.Pm10IndiaAqi]
            : measurementTypesKey[MeasurementType.Pm10]
        default:
          return measurementTypesKey[measurementType as MeasurementType]
      }
    },
    async startUserDevicesInterval(): Promise<void> {
      this.isDevicesFirstCallInProgress = true
      await this.$store.dispatch(
        `${NAMESPACES.DEVICE_INTERVALS}/${DEVICE_INTERVALS_ACTIONS.START_MANAGE_DEVICES_INTERVAL}`,
        {
          callback: this.getUserDevices,
          interval: DEVICE_UPDATE_INTERVAL_MS
        }
      )
    },
    async restartAppInterval(): Promise<void> {
      if (this.$auth.isAuthenticated && !this.$store.state.map.movingMode) {
        await this.$store.dispatch(
          `${NAMESPACES.DEVICE_INTERVALS}/${DEVICE_INTERVALS_ACTIONS.RESTART_APP_INTERVAL}`
        )
      }
    },
    openRegisterDevice(): void {
      this.dialogs.registerDevice = true
    },
    openRegisterMultipleDevices(): void {
      this.dialogs.registerMultipleDevices = true
    },
    getTimestampLocaleString(timestamp: string | number | Date): string {
      return new Date(timestamp).toLocaleString()
    },
    setDevicesTableSettings(): void {
      const save = {
        itemsPerPage: this.itemsPerPage,
        sortBy: this.sortedBy
      }
      localStorage.setItem(LOCAL_STORAGE_KEYS, JSON.stringify(save))
    },
    getDevicesTableSettings(): void {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEYS)
      if (raw === null) return
      try {
        const settings = JSON.parse(raw)
        if (settings.itemsPerPage) {
          this.itemsPerPage = settings.itemsPerPage
        }
        if (Array.isArray(settings.sortBy) && settings.sortBy.length > 0) {
          if (typeof settings.sortBy[0] === 'string') {
            // Old format: sortBy is string[], sortDesc is boolean[] — migrate to DataTableSortItem[]
            const sortDesc: boolean[] = Array.isArray(settings.sortDesc) ? settings.sortDesc : []
            this.sortedBy = settings.sortBy.map((key: string, i: number) => ({
              key,
              order: (sortDesc[i] ? 'desc' : 'asc') as 'asc' | 'desc'
            }))
          } else {
            // New format: sortBy is DataTableSortItem[]
            this.sortedBy = settings.sortBy as DataTableSortItem[]
          }
        }
      } catch {
        // Ignore malformed localStorage data, use defaults
      }
    },
    getAddDevicesDropDownProps() {
      return {
        titleText: this.$t('manageDevices.addDeviceDropdownLabel'),
        titleIcon: 'mdi-plus',
        options: [
          {
            text: this.$t('manageDevices.addDeviceOptions.single'),
            icon: 'mdi-numeric-1-box',
            action: 'single'
          },
          {
            text: this.$t('manageDevices.addDeviceOptions.multiple'),
            icon: 'mdi-plus-box-multiple',
            action: 'multiple'
          }
        ],
        onClickHandler: this.onClickAddDeviceHandler,
        closeAfterClick: true,
        closeOnClickOutside: true
      }
    },
    onClickAddDeviceHandler(action: string): void {
      switch (action) {
        case 'single':
          this.openRegisterDevice()
          break
        case 'multiple':
          this.openRegisterMultipleDevices()
          break
      }
    },
    async getUserDevices(): Promise<void> {
      await this.$store.dispatch(`${NAMESPACES.DEVICES}/${DEVICE_ACTIONS.SET_ALL_USER_DEVICES}`)
      this.isDevicesFirstCallInProgress = false
      this.updateSubscriptions()
    },
    getDeviceModel(model: string, subModel?: string): string {
      const formattedSubModel = subModel ? subModel.replace('gases', '').trim() : subModel
      if (formattedSubModel) {
        return `${model}-${formattedSubModel}`
      }

      return model
    },
    getDeviceModelName(model: string): string {
      const modelKey = model
        ? this.$store.getters[`${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_MODEL_KEY}`](
            model
          )
        : ''

      return modelKey ? deviceModelDisplay(modelKey, this.$t.bind(this)) : ''
    },
    getDeviceSubModelName(model: string, subModel?: string): string {
      const submodelKey = subModel
        ? this.$store.getters[
            `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SUBMODEL_KEY}`
          ](model, subModel)
        : ''

      return submodelKey ? deviceSubmodelDisplay(submodelKey, this.$t.bind(this)) : ''
    },
    getDeviceType(model: string, subModel?: string): string {
      const deviceModelName = this.getDeviceModelName(model)
      const deviceSubmodelName = this.getDeviceSubModelName(model, subModel)

      return deviceSubmodelName ? `${deviceModelName} (${deviceSubmodelName})` : deviceModelName
    },
    getFormattedLatestCommunication(
      lastCommunication: string | null,
      showLastCommunication: boolean
    ): string {
      if (!showLastCommunication) {
        return this.$t('manageDevices.notAvailable')
      }

      const formattedLastCommunication = validateTimestamp(
        lastCommunication,
        this.$t('manageDevices.lastCommunicationNever')
      )
      return formattedLastCommunication != this.$t('manageDevices.lastCommunicationNever')
        ? this.getTimestampLocaleString(formattedLastCommunication)
        : formattedLastCommunication
    },
    getDeviceReading(
      measurements: Record<string, number | boolean | null> | null | undefined,
      isConnected: boolean
    ): number | null {
      if (!measurements) {
        return null
      }

      if (!isConnected) {
        return null
      }

      const reading = measurements[this.measurementType]
      return typeof reading === 'number' ? reading : null
    },
    updateSubscriptions(): void {
      this.$store.dispatch(
        `${NAMESPACES.SUBSCRIPTIONS}/${SUBSCRIPTIONS_ACTIONS.UPDATE_SUBSCRIPTIONS}`,
        {
          auth: this.$auth,
          api: this.$api
        }
      )
    },
    getDeviceSubscriptionLabel(deviceId: string, showSubscription: boolean): string {
      const subscriptionTypeFromApi =
        this.$store.getters[
          `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SUBSCRIPTION_TYPE_BY_DEVICE_ID}`
        ](deviceId)

      if (!showSubscription || !subscriptionTypeFromApi) {
        return this.$t('subscriptionType.notAvailable')
      }

      if (
        subscriptionTypeFromApi == subscriptionTypes.DEVICE_SMART_BRIDGE ||
        subscriptionTypeFromApi == subscriptionTypes.STUDIES
      ) {
        return (
          this.$store.getters[
            `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_WEBSITE_SUBSCRIPTION_NAME}`
          ](deviceId) || this.$t('subscriptionType.notAvailable')
        )
      }

      return this.$t(`subscriptionType.${subscriptionTypeFromApi}`)
    },
    filterByColumnValueAndDeviceAttributes(
      value: string,
      search: string,
      item?: { raw: unknown }
    ): boolean {
      const trimmedSearch = search.trim()
      const associatedDevices = (item?.raw as { associatedDevices?: ManageDeviceRow[] } | undefined)
        ?.associatedDevices
      const foundColumnValue =
        value != null &&
        trimmedSearch != null &&
        typeof value === 'string' &&
        value.toLowerCase().indexOf(trimmedSearch.toLowerCase()) !== -1

      if (!foundColumnValue && trimmedSearch.length && value != null) {
        if (associatedDevices?.length) {
          return this.findAssociatedDevice(trimmedSearch, { associatedDevices })
        }
      }
      return foundColumnValue
    },
    findAssociatedDevice(search: string, item: { associatedDevices?: ManageDeviceRow[] }): boolean {
      const trimmedSearch = search.trim()
      for (const associatedDevice of item.associatedDevices || []) {
        for (const searchableAttribute of searchableDeviceAttributes) {
          const value = associatedDevice[searchableAttribute as keyof ManageDeviceRow]
          if (
            value != null &&
            value.toString().toLowerCase().indexOf(trimmedSearch.toLowerCase()) !== -1
          ) {
            return true
          }
        }
      }
      return false
    },
    updateTableComputedItems(computedItems: { raw: ManageDeviceRow }[]): void {
      this.tableComputedItems = computedItems.map((item) => item.raw)
      this.autoExpandAssociatedDevices()
    },
    autoExpandAssociatedDevices(): void {
      this.tableComputedItems.forEach((device) => {
        let foundSerial = false
        if (this.search && device.associatedDevices?.length) {
          foundSerial = this.findAssociatedDevice(this.search, device)
        }

        if (foundSerial) {
          this.expandAssociatedDevices(device)
        }
      })
    },
    expandAssociatedDevices(device: ManageDeviceRow | null | undefined): void {
      if (device && !this.isRowExpanded(device.id)) {
        this.expanded.push(device.id)
      }
    },
    expandAllAssociatedDevices(): void {
      this.expanded = this.expandableDevices.map((device) => device.id)
    },
    isDeviceExpandable(device: ManageDeviceRow): boolean {
      return !!device.associatedDevices?.length
    },
    collapseAllAssociatedDevices(): void {
      this.expanded = []
    },
    collapseAssociatedDevices(device: ManageDeviceRow | null | undefined): void {
      if (device && this.isRowExpanded(device.id)) {
        this.expanded = this.expanded.filter((expandedDeviceId) => expandedDeviceId !== device.id)
      }
    },
    isRowExpanded(deviceId: string): boolean {
      return this.expanded.includes(deviceId)
    },
    openDialog(dialogData: { dialog: string; deviceId: string } | null | undefined): void {
      if (dialogData) {
        this.currentDialog = dialogData.dialog
        this.currentDeviceId = dialogData.deviceId
      }
    },
    closeDialog(): void {
      this.currentDialog = ''
      this.currentDeviceId = ''
    },
    isUserAccessible(deviceId: string): boolean {
      return (
        this.accountId ===
          this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_ACCOUNT_BY_DEVICE_ID}`](
            deviceId
          ) ||
        !!this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_SHARED_BY_DEVICE_ID}`](
          deviceId
        )
      )
    }
  },
  computed: {
    devices(): ManageDeviceRow[] {
      const devices: ManageDeviceRow[] = []
      const sbDeviceQueue: ManageDeviceRow[] = []

      for (const [key, value] of Object.entries(this.$store.state.devices.allUserDevices)) {
        if (value && this.isUserAccessible(key)) {
          const model =
            this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MODEL_BY_DEVICE_ID}`](
              key
            )
          const isShared =
            this.$store.getters[
              `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_SHARED_BY_DEVICE_ID}`
            ](key)
          const subModel =
            this.$store.getters[
              `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SUBMODEL_BY_DEVICE_ID}`
            ](key)
          const lastCommunication =
            this.$store.getters[
              `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LAST_COMMUNICATION_TIME_BY_DEVICE_ID}`
            ](key)
          const gatewaySerial =
            this.$store.getters[
              `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_CONNECTED_SMART_BRIDGE_SERIAL_BY_DEVICE_ID}`
            ](key)
          const isConnected =
            this.$store.getters[
              `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_CONNECTED_BY_DEVICE_ID}`
            ](key)
          const showSubscription =
            this.$store.getters[
              `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SHOW_SUBSCRIPTION}`
            ](model)
          const showCondition =
            this.$store.getters[
              `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SHOW_CONDITION}`
            ](model)
          const showAQI =
            this.$store.getters[
              `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SHOW_AQI}`
            ](model)
          const showLastCommunication =
            this.$store.getters[
              `${NAMESPACES.DEVICE_MODELS}/${DEVICE_MODELS_GETTERS.GET_SHOW_LAST_COMMUNICATION}`
            ](model)
          const deviceName =
            this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_NAME_BY_DEVICE_ID}`](
              key
            ) || ''
          const deviceLocationName =
            this.$store.getters[
              `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_LOCATION_NAME_BY_DEVICE_ID}`
            ](key) || ''
          const deviceType = this.getDeviceType(model, subModel)
          const deviceModel = this.getDeviceModel(model, subModel).toUpperCase()
          const deviceSerial =
            this.$store.getters[`${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SERIAL_BY_DEVICE_ID}`](
              key
            )
          const deviceSubscription = this.getDeviceSubscriptionLabel(key, showSubscription)
          const formattedLastCommunication = this.getFormattedLatestCommunication(
            lastCommunication,
            showLastCommunication
          )
          const reading = this.getDeviceReading(
            this.$store.getters[
              `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_MEASUREMENTS_BY_DEVICE_ID}`
            ](key),
            isConnected
          )

          const device: ManageDeviceRow = {
            id: key,
            showSubscription,
            showCondition,
            showAQI,
            showLastCommunication,
            deviceName,
            deviceLocationName,
            accessCode: isShared ? Access.SHARED : Access.FULL,
            access: isShared
              ? this.$t('manageDevices.accessTypes.shared')
              : this.$t('manageDevices.accessTypes.owned'),
            deviceType,
            deviceModel,
            deviceSerial,
            deviceSubscription,
            lastCommunication: formattedLastCommunication,
            reading
          }

          if (gatewaySerial) {
            device.gatewaySerial = gatewaySerial
            sbDeviceQueue.push(device)
          } else {
            devices.push(device)
          }
        }
      }

      for (const sbDev of sbDeviceQueue) {
        const parentDev = devices.find((d) => d.deviceSerial === sbDev.gatewaySerial)
        if (!parentDev) {
          devices.push(sbDev)
        } else if (parentDev.associatedDevices) {
          parentDev.associatedDevices.push(sbDev)
        } else {
          parentDev.associatedDevices = [sbDev]
        }
      }

      return devices
    },
    columns(): DataTableHeader<ManageDeviceRow>[] {
      return [
        {
          key: 'data-table-expand',
          title: '',
          sortable: false
        },
        {
          title: this.$t('manageDevices.tableHeaders.deviceName'),
          align: 'start',
          key: 'deviceName'
        },
        {
          title: this.$t('manageDevices.tableHeaders.deviceLocationName'),
          align: 'start',
          key: 'deviceLocationName'
        },
        {
          title: this.$t('manageDevices.tableHeaders.deviceType'),
          key: 'deviceType'
        },
        {
          title: this.$t('manageDevices.tableHeaders.deviceModel'),
          key: 'deviceModel'
        },
        {
          title: this.$t('manageDevices.tableHeaders.deviceSerial'),
          key: 'deviceSerial'
        },
        {
          title: this.$t('manageDevices.tableHeaders.deviceSubscription'),
          key: 'deviceSubscription'
        },
        {
          title: this.$t('manageDevices.tableHeaders.access'),
          key: 'access'
        },
        {
          title: this.$t('manageDevices.tableHeaders.condition'),
          key: 'id',
          sort: (a: unknown, b: unknown) => {
            const deviceIdA = String(a)
            const deviceIdB = String(b)
            const deviceA = {
              isOwned:
                !this.$store.getters[
                  `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_SHARED_BY_DEVICE_ID}`
                ](deviceIdA),
              spsEol:
                this.$store.getters[
                  `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SPS_EOL_BY_DEVICE_ID}`
                ](deviceIdA),
              deviceErrors:
                this.$store.getters[
                  `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_DEVICE_STATUS_BY_DEVICE_ID}`
                ](deviceIdA),
              sensorErrors:
                this.$store.getters[
                  `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SENSOR_STATUS_BY_DEVICE_ID}`
                ](deviceIdA),
              rssi: this.$store.getters[
                `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_RSSI_BY_DEVICE_ID}`
              ](deviceIdA),
              isConnected:
                this.$store.getters[
                  `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_CONNECTED_BY_DEVICE_ID}`
                ](deviceIdA),
              connectionType:
                this.$store.getters[
                  `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_CONNECTION_TYPE_BY_DEVICE_ID}`
                ](deviceIdA) || ConnectionType.WIFI
            }

            const deviceB = {
              isOwned:
                !this.$store.getters[
                  `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_SHARED_BY_DEVICE_ID}`
                ](deviceIdB),
              spsEol:
                this.$store.getters[
                  `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SPS_EOL_BY_DEVICE_ID}`
                ](deviceIdB),
              deviceErrors:
                this.$store.getters[
                  `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_DEVICE_STATUS_BY_DEVICE_ID}`
                ](deviceIdB),
              sensorErrors:
                this.$store.getters[
                  `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_SENSOR_STATUS_BY_DEVICE_ID}`
                ](deviceIdB),
              rssi: this.$store.getters[
                `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_RSSI_BY_DEVICE_ID}`
              ](deviceIdB),
              isConnected:
                this.$store.getters[
                  `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_IS_CONNECTED_BY_DEVICE_ID}`
                ](deviceIdB),
              connectionType:
                this.$store.getters[
                  `${NAMESPACES.DEVICES}/${DEVICE_GETTERS.GET_CONNECTION_TYPE_BY_DEVICE_ID}`
                ](deviceIdB) || ConnectionType.WIFI
            }

            return sortDeviceCondition(deviceA, deviceB)
          }
        },
        {
          title: this.$t('manageDevices.tableHeaders.lastCommunication'),
          key: 'lastCommunication',
          sort: (a: string, b: string) => {
            const timeA = new Date(String(a)).getTime()
            const timeB = new Date(String(b)).getTime()
            if (isNaN(timeA) && isNaN(timeB)) {
              return 0
            }
            const deviceA = isNaN(timeA) ? -Infinity : timeA
            const deviceB = isNaN(timeB) ? -Infinity : timeB
            return deviceA - deviceB
          }
        },
        {
          title: this.measurementLabel,
          align: 'start',
          key: 'reading',
          sort: (a: number, b: number) => {
            const numberA = parseInt(String(a))
            const numberB = parseInt(String(b))
            const deviceA = isNaN(numberA) ? -1 : numberA
            const deviceB = isNaN(numberB) ? -1 : numberB
            return deviceA - deviceB
          }
        },
        {
          title: this.$t('manageDevices.tableHeaders.actions'),
          key: 'actions',
          sortable: false,
          align: 'end'
        }
      ]
    },
    isEmpty(): boolean {
      return !this.devices?.length
    },
    isLoading(): boolean {
      return this.$auth.loading || this.isDevicesFirstCallInProgress
    },
    isMobile(): boolean {
      return this.smAndDown
    },
    showTable(): boolean {
      return !this.isLoading && !this.isEmpty
    },
    showEmptyMessage(): boolean {
      return !this.isLoading && this.isEmpty
    },
    customTableFilter(): string {
      const trimmedSearch = this.search.trim()
      if (trimmedSearch.length >= 2) {
        return trimmedSearch
      } else if (trimmedSearch.length === 1) {
        return '//no-result//'
      }
      return ''
    },
    measurementType(): string {
      return this.getMeasurementType(
        this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`]
      )
    },
    aqiType(): number {
      return this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_AQI_TYPE}`]
    },
    measurementLabel(): string {
      const type = this.$store.getters[`${NAMESPACES.MAP}/${MAP_GETTERS.GET_MEASUREMENT_TYPE}`]
      if (type === MeasurementType.Pm10) {
        return this.$t('manageDevices.tableHeaders.aqi10')
      } else {
        return this.$t('manageDevices.tableHeaders.aqi25')
      }
    },
    tableItemComponent(): string {
      return this.isMobile ? 'DeviceTableBodyMobile' : 'DeviceTableBody'
    },
    expandableDevices(): ManageDeviceRow[] {
      return this.devices.filter((device) => {
        return this.isDeviceExpandable(device)
      })
    },
    enableExpandCollapseButtons(): boolean {
      return !!this.expandableDevices.length
    }
  }
})
</script>

<style lang="scss" scoped>
.manage-devices-card {
  .header-title {
    margin: 0;
    font-size: 1.5rem;
    font-weight: 300;
  }

  .divider {
    margin-left: 16px;
    margin-right: 16px;
    margin-bottom: 24px;
  }

  .emptyMessage {
    height: 240px;
    line-height: 240px;
    color: rgba(0, 0, 0, 0.56);
  }

  .table-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;

    background-color: transparent;
    margin-left: 16px;
    margin-right: 16px;
    margin-bottom: 16px;
    min-height: 48px;

    .search-field {
      max-width: 50%;
    }
  }

  .v-data-table {
    &:not(.mobile) :deep(.v-data-table__th:first-child) {
      width: 0 !important;
      padding-right: 0 !important;
    }
  }
}
</style>
